import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Card,
    Colors,
    Heading,
    Icon,
    MultilineInput,
    PageContainer,
    SelectFilled,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { QuestionTranslationCard } from './components/QuestionTranslationCard';

import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { companyAtom } from '@/atoms/company';
import { userInstanceAtom } from '@/atoms/userDetails';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations, useSelectedAppLanguage } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { getLanguageDisplayName, getLocalizedText, type LocalizedText } from '@/utils/localization';

interface TranslateQuestionnaireContentProps {
    questions: ReviewQuestion[];
    isLoading: boolean;
    isSaving: boolean;
    onSave: (questions: ReviewQuestion[], updatedTitle?: LocalizedText) => Promise<void>;
    onBack: () => void;
    backLabel: string;
    title: string;
    subtitle: string;
    evaluationTitle?: LocalizedText;
    titleError?: string;
    onTargetLanguageChange?: (language: string) => void;
    previewEvaluee?: User;
}

export const TranslateQuestionnaireContent = ({
    questions: initialQuestions,
    isLoading,
    isSaving,
    onSave,
    onBack,
    backLabel,
    title,
    subtitle,
    evaluationTitle,
    titleError,
    onTargetLanguageChange,
    previewEvaluee,
}: TranslateQuestionnaireContentProps) => {
    const { t } = useAmelioTranslations();
    const selectedAppLanguage = useSelectedAppLanguage();
    const company = useAtomValue(companyAtom);
    const userDetails = useAtomValue(userInstanceAtom);
    const replaceDynamicTags = useReplaceDynamicTags();
    const [targetLanguage, setTargetLanguage] = useState<string>('');
    const [localQuestions, setLocalQuestions] = useState<ReviewQuestion[]>([]);
    const [localTitle, setLocalTitle] = useState<LocalizedText>({});
    const [showValidationErrors, setShowValidationErrors] = useState<boolean>(false);

    const surveyLanguages = useMemo(() => {
        const userLanguages = userDetails?.surveyLanguages || [];
        const companyLanguages = company?.company?.surveyLanguages || [];
        return userLanguages.length > 0 ? userLanguages : companyLanguages;
    }, [userDetails, company]);

    const targetLanguageOptions = useMemo(
        () =>
            surveyLanguages.map((lang) => ({
                label: getLanguageDisplayName(lang),
                value: lang,
            })),
        [surveyLanguages],
    );

    const emptyQuestionIds = useMemo(
        () =>
            new Set(
                localQuestions
                    .filter((q) => !q.label[targetLanguage]?.trim())
                    .map((q) => q.id ?? ''),
            ),
        [localQuestions, targetLanguage],
    );

    const onQuestionLabelChange = useCallback(
        (questionId: string, value: string) => {
            setLocalQuestions((prev) =>
                prev.map((q) =>
                    q.id === questionId
                        ? { ...q, label: { ...q.label, [targetLanguage]: value } }
                        : q,
                ),
            );
        },
        [targetLanguage],
    );

    const onOptionLabelChange = useCallback(
        (questionId: string, optionId: string, value: string) => {
            setLocalQuestions((prev) =>
                prev.map((q) =>
                    q.id === questionId
                        ? {
                              ...q,
                              reviewQuestionOptions: q.reviewQuestionOptions?.map((opt) =>
                                  opt.id === optionId
                                      ? { ...opt, label: { ...opt.label, [targetLanguage]: value } }
                                      : opt,
                              ),
                          }
                        : q,
                ),
            );
        },
        [targetLanguage],
    );

    const onTitleChange = useCallback(
        (value: string) => {
            setLocalTitle((prev) => ({ ...prev, [targetLanguage]: value }));
        },
        [targetLanguage],
    );

    useEffect(() => {
        if (initialQuestions) {
            setLocalQuestions(initialQuestions);
        }
    }, [initialQuestions]);

    useEffect(() => {
        if (evaluationTitle) {
            setLocalTitle(evaluationTitle);
        }
    }, [evaluationTitle]);

    useEffect(() => {
        const isInvalid = !targetLanguage || !surveyLanguages.includes(targetLanguage);

        if (!isInvalid) {
            return;
        }

        setTargetLanguage(selectedAppLanguage);
    }, [surveyLanguages, targetLanguage, selectedAppLanguage]);

    useEffect(() => {
        if (targetLanguage) {
            onTargetLanguageChange?.(targetLanguage);
        }
    }, [targetLanguage, onTargetLanguageChange]);

    useEffect(() => {
        setShowValidationErrors(false);
    }, [targetLanguage]);

    const onSaveTranslations = async () => {
        if (emptyQuestionIds.size > 0) {
            setShowValidationErrors(true);
            return;
        }

        await onSave(localQuestions, localTitle);
    };

    if (isLoading) {
        return (
            <Skeleton className={'px-8'}>
                <Bone className={'h-15 w-50'} />

                <Bone className={'mt-20 flex h-50 w-150'} />

                <Bone className={'mt-10 flex h-500 w-full'} />
            </Skeleton>
        );
    }

    return (
        <PageContainer
            className={'flex flex-col gap-8'}
            dataTestId={'TranslateQuestionnaireContent'}
        >
            <ButtonSecondary
                dataTestId={'back-button-top'}
                onClick={onBack}
                size={'sm'}
                leftIcon={
                    <Icon
                        name={'arrow-arrow-left'}
                        className={'mr-2'}
                    />
                }
                className={'w-fit'}
            >
                {backLabel}
            </ButtonSecondary>

            <Card
                className={'max-w-[529px] self-start px-11 py-9'}
                borderRadius={'2xl'}
            >
                <Heading
                    level={3}
                    weight={'medium'}
                >
                    {title}
                </Heading>

                <Text
                    className={'mt-2'}
                    color={Colors.BASE_600}
                >
                    {subtitle}
                </Text>
            </Card>

            <Card
                className={'mt-6'}
                borderRadius={'2xl'}
                padding={'lg'}
                zIndex={20}
            >
                <Heading
                    level={4}
                    weight={'medium'}
                    className={'text-sm font-medium'}
                >
                    {t('templateManagement.templateTranslation.selectTargetLanguage')}
                </Heading>

                <div
                    className={'mt-4 flex flex-col items-center gap-4 md:flex-row md:items-center'}
                >
                    <div className={'grow'}>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div>
                                        <SelectFilled
                                            value={targetLanguage}
                                            onChange={(value: string) => setTargetLanguage(value)}
                                            options={targetLanguageOptions}
                                            variant={'white'}
                                            size={'lg'}
                                            disabled={targetLanguageOptions.length === 0}
                                            hideSelectedOption
                                            placeholder={
                                                getLanguageDisplayName(targetLanguage) ||
                                                t(
                                                    'templateManagement.templateTranslation.selectLanguagePlaceholder',
                                                )
                                            }
                                            sort={'asc'}
                                        />
                                    </div>
                                </TooltipTrigger>

                                {targetLanguageOptions.length === 0 && (
                                    <TooltipContent side={'top'}>
                                        <p>
                                            {t(
                                                'templateManagement.templateTranslation.noOtherLanguagesTooltip',
                                            )}
                                        </p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    </div>

                    <div>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className={'relative inline-block'}>
                                        <ButtonSecondary
                                            dataTestId={'auto-translate-button'}
                                            disabled
                                            className={'min-w-[200px] text-nowrap'}
                                            aria-disabled={'true'}
                                            leftIcon={
                                                <Icon
                                                    name={'security-lock'}
                                                    size={16}
                                                    color={Colors.BASE_300}
                                                    className={'mr-2'}
                                                />
                                            }
                                        >
                                            {t(
                                                'templateManagement.templateTranslation.autoTranslate',
                                            )}
                                        </ButtonSecondary>
                                    </div>
                                </TooltipTrigger>

                                <TooltipContent side={'top'}>
                                    <p>
                                        {t(
                                            'templateManagement.templateTranslation.comingSoonTooltip',
                                        )}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>

                    <div>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className={'relative inline-block'}>
                                        <ButtonSecondary
                                            dataTestId={'import-translations-button'}
                                            className={'min-w-[200px] text-nowrap'}
                                            disabled
                                            aria-disabled={'true'}
                                            leftIcon={
                                                <Icon
                                                    name={'security-lock'}
                                                    className={'mr-2'}
                                                    color={Colors.BASE_300}
                                                />
                                            }
                                        >
                                            {t(
                                                'templateManagement.templateTranslation.importTranslations',
                                            )}
                                        </ButtonSecondary>
                                    </div>
                                </TooltipTrigger>

                                <TooltipContent side={'top'}>
                                    <p>
                                        {t(
                                            'templateManagement.templateTranslation.comingSoonTooltip',
                                        )}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>

                <Text
                    size={'default'}
                    color={Colors.BASE_300}
                    className={'mt-4 mb-2 text-xs'}
                >
                    {t('templateManagement.templateTranslation.helperText')}
                </Text>

                <Text
                    color={Colors.BASE_300}
                    className={'text-xs'}
                >
                    {t('templateManagement.templateTranslation.autoTranslateHelperText')}
                </Text>
            </Card>

            {targetLanguage && evaluationTitle && (
                <Card
                    className={'mt-2'}
                    padding={'lg'}
                    borderRadius={'2xl'}
                    dataTestId={'TitleTranslationCard'}
                >
                    <div className={'flex items-center gap-3'}>
                        <div className={'bg-secondary h-1 w-6 rounded-full'} />

                        <Heading
                            level={3}
                            weight={'semibold'}
                            className={'text-lg md:text-xl'}
                        >
                            {t('templateManagement.templateTranslation.evaluationTitle')}
                        </Heading>
                    </div>

                    <Card
                        borderRadius={'2xl'}
                        padding={'lg'}
                        bgColor={'bg-white'}
                        className={'mt-4 md:mt-6'}
                    >
                        <Text
                            size={'large'}
                            weight={'semibold'}
                        >
                            {replaceDynamicTags({
                                text:
                                    localTitle[selectedAppLanguage] || getLocalizedText(localTitle),
                                employee: previewEvaluee,
                            })}
                        </Text>

                        <div
                            className={
                                'mt-6 flex w-full flex-col items-start gap-4 md:flex-row md:items-center'
                            }
                        >
                            <Text
                                weight={'medium'}
                                noWrap
                            >
                                {`${getLanguageDisplayName(targetLanguage)} :`}
                            </Text>

                            <div className={'w-full'}>
                                <MultilineInput
                                    value={localTitle[targetLanguage] || ''}
                                    onChange={(value: string) => onTitleChange(value)}
                                    placeholder={t(
                                        'templateManagement.templateTranslation.enterTranslation',
                                        { language: getLanguageDisplayName(targetLanguage) },
                                    )}
                                    variant={'white'}
                                    size={'lg'}
                                    autoGrow={true}
                                    rows={1}
                                    dataTestId={'title-translation-input'}
                                    error={!!titleError}
                                />
                            </div>
                        </div>
                    </Card>
                </Card>
            )}

            {targetLanguage && (
                <Card
                    className={'mt-2'}
                    padding={'lg'}
                    borderRadius={'2xl'}
                >
                    <div className={'flex items-center gap-3'}>
                        <div className={'bg-primary h-1 w-6 rounded-full'} />

                        <Heading
                            level={3}
                            weight={'semibold'}
                            className={'text-xl md:text-xl'}
                        >
                            {t('templateManagement.templateTranslation.questionsToTranslate')}
                        </Heading>
                    </div>

                    <Text className={'mt-6'}>
                        {t('templateManagement.templateTranslation.questionsHelperText')}
                    </Text>

                    <div className={'mt-8 flex flex-col gap-8'}>
                        {localQuestions.map((question) => (
                            <QuestionTranslationCard
                                key={question.id}
                                question={question}
                                defaultLanguage={selectedAppLanguage}
                                targetLanguage={targetLanguage}
                                onQuestionLabelChange={onQuestionLabelChange}
                                onOptionLabelChange={onOptionLabelChange}
                                previewEvaluee={previewEvaluee}
                                hasError={
                                    showValidationErrors && emptyQuestionIds.has(question.id ?? '')
                                }
                            />
                        ))}
                    </div>
                </Card>
            )}

            <div className={'mt-4 flex flex-wrap items-start justify-between gap-4 md:gap-0'}>
                <ButtonSecondary
                    dataTestId={'back-button'}
                    onClick={onBack}
                    leftIcon={
                        <Icon
                            name={'arrow-arrow-left'}
                            className={'mr-2'}
                        />
                    }
                >
                    {t('common.back')}
                </ButtonSecondary>

                <div className={'flex flex-col items-end gap-2'}>
                    {showValidationErrors && emptyQuestionIds.size > 0 && (
                        <div data-testid={'question-translation-validation-error'}>
                            <Text
                                size={'small'}
                                color={Colors.ERROR}
                            >
                                {t('templateManagement.templateTranslation.questionTitleRequired')}
                            </Text>
                        </div>
                    )}

                    <ButtonGradientPrimary
                        dataTestId={'save-translations-button'}
                        onClick={onSaveTranslations}
                        loading={isSaving}
                    >
                        {t('templateManagement.templateTranslation.saveTranslations')}
                    </ButtonGradientPrimary>
                </div>
            </div>
        </PageContainer>
    );
};
